import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PeriodProvider } from './contexts/PeriodContext';
import { Toaster } from './components/ui/sonner';
import { AppShell } from './components/app/AppShell';

import LoginPage           from './pages/LoginPage';
import ChangePasswordPage  from './pages/ChangePasswordPage';
import PendingActivationPage from './pages/PendingActivationPage';
import BreakGlassPage      from './pages/BreakGlassPage';
import DashboardPage       from './pages/DashboardPage';
import UsersPage           from './pages/UsersPage';
import CardholdersPage     from './pages/CardholdersPage';
import CardsPage           from './pages/CardsPage';
import CategoriesPage      from './pages/CategoriesPage';
import CostCentersPage     from './pages/CostCentersPage';
import PeriodsPage         from './pages/PeriodsPage';
import PeriodProgressPage  from './pages/PeriodProgressPage';
import ImportPage          from './pages/ImportPage';
import WorkbenchPage       from './pages/WorkbenchPage';
import MyTransactionsPage  from './pages/MyTransactionsPage';
import ExceptionsPage      from './pages/ExceptionsPage';
import ApprovalsPage       from './pages/ApprovalsPage';
import RulesPage           from './pages/RulesPage';
import RecurringPatternsPage from './pages/RecurringPatternsPage';
import ExportsPage         from './pages/ExportsPage';
import AnalyticsPage       from './pages/AnalyticsPage';
import AuditLogPage        from './pages/AuditLogPage';
import SettingsPage        from './pages/SettingsPage';

const ADMIN_ONLY  = ['finance_admin'];
const REVIEWER_UP = ['finance_admin', 'finance_reviewer'];
const ALL_ROLES   = ['finance_admin', 'finance_reviewer', 'cardholder', 'auditor'];

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  // SSO users sit in role='pending' until an admin assigns them a real role.
  // Send them to a dedicated holding page rather than the workbench, where
  // every API call would 403.
  if (user.role === 'pending') return <Navigate to="/pending" replace />;
  return children;
}

// Role gate. Mismatched roles bounce to /dashboard before the page mounts,
// so a cardholder typing /users into the URL bar never fires an API call.
function RequireRole({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

const APP_ROUTES = [
  { path: '/dashboard',          element: <DashboardPage />,         roles: ALL_ROLES },
  { path: '/users',              element: <UsersPage />,             roles: ADMIN_ONLY },
  { path: '/cardholders',        element: <CardholdersPage />,       roles: ADMIN_ONLY },
  { path: '/cards',              element: <CardsPage />,             roles: ADMIN_ONLY },
  { path: '/categories',         element: <CategoriesPage />,        roles: ADMIN_ONLY },
  { path: '/cost-centers',       element: <CostCentersPage />,       roles: ADMIN_ONLY },
  { path: '/periods',            element: <PeriodsPage />,           roles: ADMIN_ONLY },
  { path: '/period-progress',    element: <PeriodProgressPage />,    roles: REVIEWER_UP },
  { path: '/import',             element: <ImportPage />,            roles: ADMIN_ONLY },
  { path: '/workbench',          element: <WorkbenchPage />,         roles: REVIEWER_UP },
  { path: '/my-transactions',    element: <MyTransactionsPage />,    roles: ['finance_admin', 'cardholder'] },
  { path: '/exceptions',         element: <ExceptionsPage />,        roles: REVIEWER_UP },
  { path: '/approvals',          element: <ApprovalsPage />,         roles: REVIEWER_UP },
  { path: '/rules',              element: <RulesPage />,             roles: ADMIN_ONLY },
  { path: '/recurring-patterns', element: <RecurringPatternsPage />, roles: REVIEWER_UP },
  { path: '/exports',            element: <ExportsPage />,           roles: ADMIN_ONLY },
  { path: '/analytics',          element: <AnalyticsPage />,         roles: ['finance_admin', 'finance_reviewer', 'auditor'] },
  { path: '/audit',              element: <AuditLogPage />,          roles: ['finance_admin', 'finance_reviewer', 'auditor'] },
  { path: '/settings',           element: <SettingsPage />,          roles: ADMIN_ONLY },
];

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login"           element={<LoginPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/pending"         element={<PendingActivationPage />} />
      <Route path="/break-glass"     element={<BreakGlassPage />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <PeriodProvider>
              <AppShell>
                <Routes>
                  {APP_ROUTES.map(r => (
                    <Route
                      key={r.path}
                      path={r.path}
                      element={<RequireRole roles={r.roles}>{r.element}</RequireRole>}
                    />
                  ))}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </AppShell>
            </PeriodProvider>
          </RequireAuth>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
